import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Stack, Box, CircularProgress, Card, CardContent } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useUI } from '@platform/core/src/context/UIContext';
import { useConfig } from '@platform/core/src/context/ConfigContext';
import { useApi } from '@platform/core/src/context/ApiContext';
import { updateWorkoutSession } from '../../services/workoutSessionApi';

const SwitchTeamMessage = ({ targetTeam, payload }) => {
  const { initializeSession, isInitializing } = useSession();
  const { closeAllResourceTabs, openResourceTab } = useUI();
  const { teamSidebarConfigs, startSessionButtons } = useConfig();
  const { resource: resourceApi } = useApi();

  const [isSwitching, setIsSwitching] = useState(false);
  const [targetResourceName, setTargetResourceName] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (payload?.workout_session_id) {
      resourceApi.fetchResource('workout_session', payload.workout_session_id)
        .then(res => {
          if (isMounted && res?.name) {
            setTargetResourceName(res.name);
          }
        })
        .catch(err => {
          console.warn("Could not fetch workout session name for display:", err);
        });
    }
    return () => { isMounted = false; };
  }, [payload, resourceApi]);

  const mapPayloadToContext = (data) => {
    const context = [];
    if (!data) return context;

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'team_name') return;

      // Handle resource IDs (e.g., location_id, workout_plan_id)
      if (key.endsWith('_id') && typeof value === 'string') {
        const type = key.replace('_id', '');
        context.push({ id: value, type: type });
      } else {
        // Handle metadata (e.g., notes, muscle_groups)
        const stringVal = typeof value === 'string' ? value : JSON.stringify(value);
        
        // If the key is 'notes' or 'instruction', pass the value directly as the note content.
        // Otherwise, prefix it with the key name so the agent knows what the data represents.
        const noteContent = (key === 'notes' || key === 'instruction')
            ? stringVal
            : `${key.replace(/_/g, ' ')}: ${stringVal}`;

        context.push({
            id: `event_metadata_${key}`,
            type: 'metadata',
            data: { [key]: value },
            notes: noteContent
        });
      }
    });
    return context;
  };

  const handleSwitch = async () => {
    setIsSwitching(true);
    try {
      if (targetTeam === 'active_workout_team') {
        closeAllResourceTabs();
      }

      const initialContext = mapPayloadToContext(payload);
      const buttonConfig = startSessionButtons?.find(btn => btn.teamName === targetTeam);
      let preChatConfig = buttonConfig?.preChatConfig || null;

      let chatSessionIdToResume = null;
      let workoutSessionToUpdate = null;

      if (targetTeam === 'active_workout_team' && payload?.workout_session_id) {
          preChatConfig = null;
          try {
              const workoutSession = await resourceApi.fetchResource('workout_session', payload.workout_session_id);
              if (workoutSession) {
                  openResourceTab(workoutSession);
                  workoutSessionToUpdate = workoutSession;
                  if (workoutSession.chat_session_id) {
                      chatSessionIdToResume = workoutSession.chat_session_id;
                  }
              }
          } catch (e) {
              console.error("Failed to fetch workout session for switch:", e);
          }
      }

      const sessionOptions = {
        team_name: targetTeam,
        context: initialContext,
        preChatConfig: preChatConfig
      };

      let newSessionId = null;
      if (chatSessionIdToResume) {
          try {
             newSessionId = await initializeSession(chatSessionIdToResume, sessionOptions);
          } catch (e) {
             console.warn("Failed to resume chat session, creating new one.", e);
             newSessionId = await initializeSession(null, sessionOptions);
          }
      } else {
          newSessionId = await initializeSession(null, sessionOptions);
      }

      if (newSessionId && workoutSessionToUpdate && (!workoutSessionToUpdate.chat_session_id || workoutSessionToUpdate.chat_session_id !== newSessionId)) {
          try {
              await updateWorkoutSession(workoutSessionToUpdate.id, { chat_session_id: newSessionId });
          } catch (e) {
              console.error("Failed to link new chat session to workout session:", e);
          }
      }

    } catch (error) {
      console.error('Failed to switch team:', error);
    } finally {
        setIsSwitching(false);
    }
  };

  const teamLabel = useMemo(() => {
    if (teamSidebarConfigs && teamSidebarConfigs[targetTeam]) {
      return teamSidebarConfigs[targetTeam].title;
    }
    return targetTeam
      .replace(/_/g, ' ')
      .replace('team', '')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }, [targetTeam, teamSidebarConfigs]);

  const isLoading = isInitializing || isSwitching;

  return (
    <Card variant="outlined" sx={{ maxWidth: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" color="primary">
            Ready to Switch Activities?
          </Typography>
          <Typography variant="body1">
            The assistant suggests moving to <strong>{targetResourceName || teamLabel}</strong> to continue.
          </Typography>
          <Box>
            <Button
              variant="contained"
              onClick={handleSwitch}
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
            >
              Switch to {teamLabel}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

SwitchTeamMessage.propTypes = {
  targetTeam: PropTypes.string.isRequired,
  payload: PropTypes.object,
};

export default SwitchTeamMessage;